import { Button, Heading, Icon, Spinner, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useIsDesktop } from '../../../hooks/use-media-query';
import { AddFileModal } from '../components/add-file-modal';
import { EditorPane } from '../components/editor-pane';
import { EditorSidebar } from '../components/editor-sidebar';
import { ThemeEditorProvider, useThemeEditor } from '../context/theme-editor-context';

export const ThemeEditorPage = withPageProviders(ThemeEditorPageContent);

function ThemeEditorPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const themeId = navigationHandler.params().themeId;
  // Read once; tab state is owned by the editor store after load.
  const [initialOpen] = useState<string[]>(() => navigationHandler.query().open?.split(',') ?? []);

  useEffect(() => {
    if (!themeId) {
      notificationHandler.error(t('features:themes.pages.theme_editor_page.unknown_theme'));
    }
  }, [themeId, notificationHandler]);

  if (!themeId) {
    return null;
  }

  return (
    <ThemeEditorProvider themeId={themeId} initialOpen={initialOpen}>
      <ThemeEditorBody />
    </ThemeEditorProvider>
  );
}

function ThemeEditorBody() {
  const { theme, initialising, unsavedCount } = useThemeEditor();
  const { navigationHandler } = useHandlers();
  const [addFileOpen, setAddFileOpen] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (isDesktop) {
      setSidebarOpen(false);
    }
  }, [isDesktop]);

  // Warn before leaving the browser tab/refresh with unsaved changes.
  useEffect(() => {
    if (unsavedCount === 0) {
      return;
    }
    function beforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [unsavedCount]);

  if (initialising) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const hasFiles = (theme?.files.length ?? 0) > 0;

  if (!hasFiles) {
    return (
      <div className="flex size-full items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-center">
          <Icon name="document" size="xl" />
          <Heading level="h3" style="md">
            {t('features:themes.pages.theme_editor_page.empty_title', { name: theme?.name ?? '' })}
          </Heading>
          <Text variant="secondary">
            {t('features:themes.pages.theme_editor_page.empty_subtitle')}
          </Text>
          <div className="mt-2 flex gap-3">
            <Button
              variant="secondary"
              onClick={() => navigationHandler.navigate('/configuration/theme')}
            >
              {t('features:themes.pages.theme_editor_page.back_to_templates')}
            </Button>
            <Button icon="plusCircle" onClick={() => setAddFileOpen(true)}>
              {t('features:themes.pages.theme_editor_page.add_file_button')}
            </Button>
          </div>
        </div>
        <AddFileModal isOpen={addFileOpen} onClose={() => setAddFileOpen(false)} />
      </div>
    );
  }

  return (
    <div className="relative flex size-full overflow-hidden bg-white">
      <EditorSidebar
        onAddFile={() => setAddFileOpen(true)}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <EditorPane onOpenSidebar={() => setSidebarOpen(true)} />
      <AddFileModal isOpen={addFileOpen} onClose={() => setAddFileOpen(false)} />
    </div>
  );
}
